import React, { useState } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, Chip, Button,
  Tabs, Tab, Divider, LinearProgress, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import { Analytics, Assignment, School, TrendingUp, TrendingDown, Download, People } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';

const AnalyticsAdmin = () => {
  const location = useLocation();
  const getInitialTab = () => {
    if (location.pathname.includes('/applications')) return 1;
    if (location.pathname.includes('/students')) return 2;
    return 0;
  };

  const [tab, setTab] = useState(getInitialTab());
  const [year, setYear] = useState('2023-24');

  const statCards = [
    { label: 'Total Applications', value: 45, change: '+18%', up: true, color: 'primary.main' },
    { label: 'Acceptance Rate', value: '62%', change: '+5%', up: true, color: 'success.main' },
    { label: 'Total Students', value: 234, change: '+12', up: true, color: 'info.main' },
    { label: 'Avg GPA', value: '3.4', change: '+0.1', up: true, color: 'warning.main' },
    { label: 'Avg Attendance', value: '95.2%', change: '-0.3%', up: false, color: 'success.main' },
    { label: 'Staff Satisfaction', value: '4.7/5', change: '+0.2', up: true, color: 'secondary.main' },
  ];

  const appFunnel = [
    { stage: 'Inquiries Received', count: 82, color: '#1976d2' },
    { stage: 'Applications Started', count: 61, color: '#388e3c' },
    { stage: 'Applications Submitted', count: 45, color: '#f57c00' },
    { stage: 'Under Review', count: 38, color: '#7b1fa2' },
    { stage: 'Approved', count: 28, color: '#388e3c' },
    { stage: 'Enrolled', count: 22, color: '#00796b' },
  ];

  const gradeEnrollment = [
    { grade: 'Kindergarten', count: 18, capacity: 20 },
    { grade: '1st Grade', count: 22, capacity: 25 },
    { grade: '2nd Grade', count: 21, capacity: 25 },
    { grade: '3rd Grade', count: 24, capacity: 25 },
    { grade: '4th Grade', count: 23, capacity: 25 },
    { grade: '5th Grade', count: 25, capacity: 25 },
    { grade: '6th Grade', count: 24, capacity: 28 },
    { grade: '7th Grade', count: 22, capacity: 28 },
    { grade: '8th Grade', count: 20, capacity: 28 },
    { grade: '9th Grade', count: 15, capacity: 30 },
  ];

  const gradePerformance = [
    { subject: 'Mathematics', avg: 82, prev: 79 },
    { subject: 'English Language Arts', avg: 86, prev: 84 },
    { subject: 'Science', avg: 84, prev: 82 },
    { subject: 'History', avg: 81, prev: 83 },
    { subject: 'Bible Studies', avg: 91, prev: 90 },
    { subject: 'Physical Education', avg: 95, prev: 94 },
  ];

  const appByMonth = [
    { month: 'Aug', count: 3 }, { month: 'Sep', count: 4 }, { month: 'Oct', count: 5 },
    { month: 'Nov', count: 6 }, { month: 'Dec', count: 8 }, { month: 'Jan', count: 12 },
    { month: 'Feb', count: 7 },
  ];
  const maxApps = Math.max(...appByMonth.map(m => m.count));

  const appByGrade = [
    { grade: 'K', count: 8 }, { grade: '1st', count: 6 }, { grade: '2nd', count: 5 },
    { grade: '3rd', count: 7 }, { grade: '4th', count: 4 }, { grade: '5th', count: 5 },
    { grade: '6th', count: 4 }, { grade: '7th', count: 3 }, { grade: '8th', count: 2 }, { grade: '9th', count: 1 },
  ];
  const maxGradeApps = Math.max(...appByGrade.map(g => g.count));

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Analytics & Reports</Typography>
          <Typography variant="body2" color="text.secondary">School performance and enrollment analytics</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Year</InputLabel>
            <Select value={year} label="Year" onChange={e => setYear(e.target.value)}>
              <MenuItem value="2023-24">2023-24</MenuItem>
              <MenuItem value="2022-23">2022-23</MenuItem>
              <MenuItem value="2021-22">2021-22</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" startIcon={<Download />}>Export</Button>
        </Box>
      </Box>

      <Paper elevation={2} sx={{ borderRadius: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tab} onChange={(e, v) => setTab(v)}>
            <Tab icon={<Analytics />} label="Overview" iconPosition="start" />
            <Tab icon={<Assignment />} label="Application Reports" iconPosition="start" />
            <Tab icon={<School />} label="Student Reports" iconPosition="start" />
          </Tabs>
        </Box>

        {/* Overview Tab */}
        {tab === 0 && (
          <Box sx={{ p: 3 }}>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {statCards.map((s, i) => (
                <Grid item xs={6} md={4} lg={2} key={i}>
                  <Card elevation={1} sx={{ borderRadius: 2, height: '100%' }}>
                    <CardContent sx={{ py: 2 }}>
                      <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: s.color, my: 0.5 }}>{s.value}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {s.up ? <TrendingUp sx={{ fontSize: 14, color: 'success.main' }} /> : <TrendingDown sx={{ fontSize: 14, color: 'error.main' }} />}
                        <Typography variant="caption" color={s.up ? 'success.main' : 'error.main'}>{s.change} vs last year</Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card elevation={1} sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Enrollment by Grade</Typography>
                    {gradeEnrollment.map(g => (
                      <Box key={g.grade} sx={{ mb: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2">{g.grade}</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{g.count} / {g.capacity}</Typography>
                        </Box>
                        <LinearProgress variant="determinate" value={(g.count / g.capacity) * 100} sx={{ height: 8, borderRadius: 4 }} color={g.count >= g.capacity ? 'error' : g.count / g.capacity >= 0.85 ? 'warning' : 'primary'} />
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card elevation={1} sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Academic Performance by Subject</Typography>
                    {gradePerformance.map(s => (
                      <Box key={s.subject} sx={{ mb: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2">{s.subject}</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.avg}%</Typography>
                            {s.avg > s.prev
                              ? <TrendingUp sx={{ fontSize: 14, color: 'success.main' }} />
                              : <TrendingDown sx={{ fontSize: 14, color: 'error.main' }} />}
                          </Box>
                        </Box>
                        <LinearProgress variant="determinate" value={s.avg} sx={{ height: 8, borderRadius: 4 }} color={s.avg >= 90 ? 'success' : s.avg >= 80 ? 'primary' : 'warning'} />
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Application Reports Tab */}
        {tab === 1 && (
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card elevation={1} sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Applications by Month</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 160 }}>
                      {appByMonth.map(m => (
                        <Box key={m.month} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>{m.count}</Typography>
                          <Box sx={{ width: '100%', height: `${(m.count / maxApps) * 120}px`, bgcolor: 'primary.main', borderRadius: '4px 4px 0 0', minHeight: 4 }} />
                          <Typography variant="caption" color="text.secondary">{m.month}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card elevation={1} sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Application Funnel</Typography>
                    {appFunnel.map((f, i) => (
                      <Box key={f.stage} sx={{ mb: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2">{f.stage}</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{f.count}</Typography>
                        </Box>
                        <Box sx={{ height: 10, bgcolor: 'grey.200', borderRadius: 5, overflow: 'hidden' }}>
                          <Box sx={{ height: '100%', width: `${(f.count / appFunnel[0].count) * 100}%`, bgcolor: f.color, borderRadius: 5 }} />
                        </Box>
                      </Box>
                    ))}
                    <Divider sx={{ my: 1.5 }} />
                    <Typography variant="body2" color="text.secondary">
                      Conversion rate: <strong>{Math.round((appFunnel[appFunnel.length - 1].count / appFunnel[0].count) * 100)}%</strong> of inquiries enrolled
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card elevation={1} sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Applications by Grade Applied For</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 140 }}>
                      {appByGrade.map(g => (
                        <Box key={g.grade} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>{g.count}</Typography>
                          <Box sx={{ width: '100%', height: `${(g.count / maxGradeApps) * 100}px`, bgcolor: 'success.main', borderRadius: '4px 4px 0 0', minHeight: 4 }} />
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>{g.grade}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card elevation={1} sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Application Status Summary</Typography>
                    {[
                      { label: 'Total Applications', value: 45, color: 'primary.main' },
                      { label: 'Approved', value: 28, color: 'success.main' },
                      { label: 'Pending Review', value: 9, color: 'warning.main' },
                      { label: 'Rejected', value: 5, color: 'error.main' },
                      { label: 'Waitlist', value: 3, color: 'info.main' },
                    ].map(s => (
                      <Box key={s.label} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="body2">{s.label}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: s.color }}>{s.value}</Typography>
                      </Box>
                    ))}
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">Acceptance Rate: <strong style={{ color: '#4caf50' }}>62.2%</strong></Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Student Reports Tab */}
        {tab === 2 && (
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Card elevation={1} sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>GPA Distribution</Typography>
                    {[
                      { label: '4.0 (A+)', count: 12, color: '#4caf50' },
                      { label: '3.5 – 3.9 (A)', count: 45, color: '#8bc34a' },
                      { label: '3.0 – 3.4 (B+)', count: 68, color: '#ffeb3b' },
                      { label: '2.5 – 2.9 (B)', count: 71, color: '#ff9800' },
                      { label: '2.0 – 2.4 (C)', count: 28, color: '#f44336' },
                      { label: 'Below 2.0', count: 10, color: '#9e9e9e' },
                    ].map(g => (
                      <Box key={g.label} sx={{ mb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                          <Typography variant="body2">{g.label}</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{g.count}</Typography>
                        </Box>
                        <Box sx={{ height: 8, bgcolor: 'grey.200', borderRadius: 4, overflow: 'hidden' }}>
                          <Box sx={{ height: '100%', width: `${(g.count / 234) * 100}%`, bgcolor: g.color, borderRadius: 4 }} />
                        </Box>
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card elevation={1} sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Attendance Overview</Typography>
                    {[
                      { label: '98 – 100%', count: 42, color: '#4caf50' },
                      { label: '95 – 97%', count: 89, color: '#8bc34a' },
                      { label: '90 – 94%', count: 61, color: '#ff9800' },
                      { label: '85 – 89%', count: 28, color: '#f57c00' },
                      { label: 'Below 85%', count: 14, color: '#f44336' },
                    ].map(g => (
                      <Box key={g.label} sx={{ mb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                          <Typography variant="body2">{g.label}</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{g.count}</Typography>
                        </Box>
                        <Box sx={{ height: 8, bgcolor: 'grey.200', borderRadius: 4, overflow: 'hidden' }}>
                          <Box sx={{ height: '100%', width: `${(g.count / 234) * 100}%`, bgcolor: g.color, borderRadius: 4 }} />
                        </Box>
                      </Box>
                    ))}
                    <Divider sx={{ my: 1.5 }} />
                    <Typography variant="body2" color="text.secondary">School-wide average: <strong>95.2%</strong></Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card elevation={1} sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Student Status</Typography>
                    {[
                      { label: 'Enrolled', value: 218, color: 'success.main' },
                      { label: 'Academic Probation', value: 8, color: 'warning.main' },
                      { label: 'Inactive / Withdrawn', value: 8, color: 'error.main' },
                      { label: 'New This Term', value: 22, color: 'info.main' },
                    ].map(s => (
                      <Box key={s.label} sx={{ display: 'flex', justifyContent: 'space-between', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="body2">{s.label}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: s.color }}>{s.value}</Typography>
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {['Honor Roll Report', 'Attendance Summary', 'Academic Probation List', 'Grade Level Performance'].map(r => (
                    <Button key={r} variant="outlined" startIcon={<Download />}>{r}</Button>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default AnalyticsAdmin;
